import React, { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, RotateCcw, RefreshCw, X } from 'lucide-react';
import { t } from '../../utils/i18n';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import DateTimePicker from '../../components/DateTimePicker/DateTimePicker';
import FormField from '../../components/FormField/FormField';
import PlatformBadge from '../../components/PlatformBadge/PlatformBadge';
import PaginatedTableCard from '../../components/PaginatedTableCard/PaginatedTableCard';
import {
  PLATFORMS,
  RELEASE_STAGES,
  listAppVersions,
  createAppVersion,
  updateAppVersion,
  deleteAppVersion,
  restoreAppVersion,
} from '../../api/appVersion';

const PAGE_SIZE = 10;
const APP_VERSION_SKELETON_WIDTHS = ['w-10', 'w-16', 'w-24', 'w-20', 'w-20', 'w-32', 'w-28', 'w-28', 'w-16 ml-auto'];

function formatDateTime(value) {
  if (!value) return '-';
  const str = String(value);
  return str.replace('T', ' ').slice(0, 19);
}

function toDateTimeLocal(isoOrNull) {
  if (!isoOrNull) return '';
  const d = new Date(isoOrNull);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function fromDateTimeLocal(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function stageBadgeClasses(stage) {
  switch (stage) {
    case 'RELEASED':
      return 'bg-emerald-500/10 text-emerald-300';
    case 'IN_REVIEW':
      return 'bg-primary/10 text-primary';
    case 'DEPRECATED':
      return 'bg-red-500/10 text-red-400';
    case 'DRAFT':
    default:
      return 'bg-dark-surface text-dark-text-secondary';
  }
}

const EMPTY_FORM = {
  platform: 'IOS',
  build_number: '',
  version_name: '',
  release_stage: 'DRAFT',
  title: '',
  description: '',
  internal_note: '',
  released_at: '',
};

function toFormValues(row) {
  if (!row) return { ...EMPTY_FORM };
  return {
    platform: row.platform ?? 'IOS',
    build_number: row.build_number ?? '',
    version_name: row.version_name ?? '',
    release_stage: row.release_stage ?? 'DRAFT',
    title: row.title ?? '',
    description: row.description ?? '',
    internal_note: row.internal_note ?? '',
    released_at: toDateTimeLocal(row.released_at),
  };
}

function toPayload(values) {
  return {
    platform: values.platform,
    build_number: values.build_number,
    version_name: values.version_name,
    release_stage: values.release_stage,
    title: values.title,
    description: values.description,
    internal_note: values.internal_note,
    released_at: fromDateTimeLocal(values.released_at),
  };
}

export default function AppVersion() {
  const { user } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const queryClient = useQueryClient();

  const [platform, setPlatform] = useState('ALL');
  const [releaseStage, setReleaseStage] = useState('ALL');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);

  const queryKey = useMemo(
    () => ['appVersions', apiEnvironment, { platform, releaseStage, includeDeleted, search, page }],
    [apiEnvironment, platform, releaseStage, includeDeleted, search, page],
  );

  const listQuery = useQuery({
    queryKey,
    queryFn: () =>
      listAppVersions({
        platform,
        releaseStage,
        includeDeleted,
        search,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['appVersions', apiEnvironment] });

  const createMutation = useMutation({
    mutationFn: (payload) => createAppVersion(payload, { actorUserId: user?.id }),
    onSuccess: () => {
      invalidate();
      closeModal();
    },
    onError: (err) => setFormError(err?.message || 'Failed to save'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateAppVersion(id, payload, { actorUserId: user?.id }),
    onSuccess: () => {
      invalidate();
      closeModal();
    },
    onError: (err) => setFormError(err?.message || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteAppVersion(id, { actorUserId: user?.id }),
    onSuccess: invalidate,
  });

  const restoreMutation = useMutation({
    mutationFn: (id) => restoreAppVersion(id, { actorUserId: user?.id }),
    onSuccess: invalidate,
    onError: (err) => alert(err?.message || 'Failed to restore'),
  });

  useEffect(() => {
    setPage(1);
  }, [platform, releaseStage, includeDeleted, search]);

  const { items = [], total_count: totalCount = 0, page: currentPage = 1 } =
    listQuery.data || {};
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const openCreate = () => {
    setEditing(null);
    setFormValues({ ...EMPTY_FORM });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormValues(toFormValues(row));
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setFormError(null);
  };

  const VERSION_RE = /^\d+\.\d+\.\d+$/;

  const versionNameError = (() => {
    const v = formValues.version_name?.trim();
    if (!v) return null;
    return VERSION_RE.test(v) ? null : t('appVersion.form.errorVersionFormat');
  })();

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError(null);
    if (!formValues.version_name?.trim()) {
      setFormError(t('appVersion.form.errorVersionRequired'));
      return;
    }
    if (!VERSION_RE.test(formValues.version_name.trim())) {
      setFormError(t('appVersion.form.errorVersionFormat'));
      return;
    }
    const payload = toPayload(formValues);
    if (editing) {
      updateMutation.mutate({ id: editing.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (row) => {
    const confirmMsg = t('appVersion.confirm.delete', { version: row.version_name });
    if (!window.confirm(confirmMsg)) return;
    deleteMutation.mutate(row.id);
  };

  const handleRestore = (row) => {
    restoreMutation.mutate(row.id);
  };

  const handleRefresh = () => {
    listQuery.refetch();
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="min-h-screen py-8 px-3 sm:px-5 lg:px-6 bg-dark-bg">
      <div className="w-full max-w-[min(100%,1920px)] mx-auto">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold text-dark-text">{t('appVersion.title')}</h1>
            <p className="text-dark-text-secondary text-sm mt-1">
              {t('appVersion.description')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={listQuery.isRefetching}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dark-border text-dark-text hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
              title={t('appVersion.actions.refresh')}
            >
              <RefreshCw className={`w-4 h-4 ${listQuery.isRefetching ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">{t('appVersion.actions.refresh')}</span>
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-black text-sm font-medium hover:bg-highlight transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('appVersion.actions.add')}
            </button>
          </div>
        </div>

        <div className="bg-dark-card border border-dark-border rounded-lg p-4 mb-4 flex flex-wrap items-center gap-3">
          <label className="text-xs text-dark-text-secondary flex items-center gap-2">
            <span>{t('appVersion.filter.platform')}</span>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="bg-dark-surface border border-dark-border text-xs text-dark-text-secondary rounded-md px-2 py-1"
            >
              <option value="ALL">{t('appVersion.filter.all')}</option>
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-dark-text-secondary flex items-center gap-2">
            <span>{t('appVersion.filter.stage')}</span>
            <select
              value={releaseStage}
              onChange={(e) => setReleaseStage(e.target.value)}
              className="bg-dark-surface border border-dark-border text-xs text-dark-text-secondary rounded-md px-2 py-1"
            >
              <option value="ALL">{t('appVersion.filter.all')}</option>
              {RELEASE_STAGES.map((s) => (
                <option key={s} value={s}>
                  {t(`appVersion.stage.${s}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-dark-text-secondary flex items-center gap-2 ml-auto">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              className="accent-primary"
            />
            <span>{t('appVersion.filter.showDeleted')}</span>
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('appVersion.filter.searchPlaceholder')}
            className="bg-dark-surface border border-dark-border text-xs text-dark-text placeholder-dark-text-muted rounded-md px-3 py-1.5 w-64"
          />
        </div>

        {listQuery.isError && (
          <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {listQuery.error?.message || 'Failed to load app versions'}
          </div>
        )}

        <PaginatedTableCard
          error={listQuery.error}
          dataUpdatedAt={listQuery.dataUpdatedAt}
          isPending={listQuery.isPending}
          isFetching={listQuery.isFetching}
          isEmpty={!listQuery.isPending && items.length === 0}
          skeletonRows={PAGE_SIZE}
          skeletonCellWidths={APP_VERSION_SKELETON_WIDTHS}
          colSpan={9}
          minHeight="min-h-[28.5rem]"
          tableClassName="w-full text-sm"
          loadingLabel={t('common.loading')}
          emptyLabel={t('appVersion.table.empty')}
          page={page}
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setPage}
          prevLabel={t('user.pagination.prev')}
          nextLabel={t('user.pagination.next')}
          summary={t('user.pagination.page', { page: currentPage, totalPages })}
          header={(
            <tr className="border-b border-dark-border text-xs text-dark-text-secondary">
              <th className="text-left py-3 px-4">ID</th>
              <th className="text-left py-3 px-4">{t('appVersion.table.platform')}</th>
              <th className="text-left py-3 px-4">{t('appVersion.table.versionName')}</th>
              <th className="text-left py-3 px-4">{t('appVersion.table.buildNumber')}</th>
              <th className="text-left py-3 px-4">{t('appVersion.table.stage')}</th>
              <th className="text-left py-3 px-4">{t('appVersion.table.title')}</th>
              <th className="text-left py-3 px-4">{t('appVersion.table.releasedAt')}</th>
              <th className="text-left py-3 px-4">{t('appVersion.table.updatedAt')}</th>
              <th className="text-right py-3 px-4">{t('appVersion.table.actions')}</th>
            </tr>
          )}
        >
          {items.map((row) => {
            const isDeleted = row.deleted_at != null;
            return (
              <tr
                key={row.id}
                className={[
                  'border-b border-dark-border last:border-0 hover:bg-dark-surface/50',
                  isDeleted ? 'opacity-60' : '',
                ].join(' ')}
              >
                <td className="py-3 px-4 text-dark-text-secondary text-xs">{row.id}</td>
                <td className="py-3 px-4">
                  <PlatformBadge platform={row.platform} />
                </td>
                <td className="py-3 px-4 text-dark-text font-medium">
                  {row.version_name}
                  {isDeleted && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400">
                      {t('appVersion.badge.deleted')}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-dark-text-secondary text-xs">
                  {row.build_number ?? '-'}
                </td>
                <td className="py-3 px-4">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${stageBadgeClasses(row.release_stage)}`}
                  >
                    {t(`appVersion.stage.${row.release_stage}`)}
                  </span>
                </td>
                <td className="py-3 px-4 text-dark-text-secondary text-xs max-w-[260px] truncate" title={row.title || ''}>
                  {row.title ?? '-'}
                </td>
                <td className="py-3 px-4 text-dark-text-secondary text-xs">
                  {formatDateTime(row.released_at)}
                </td>
                <td className="py-3 px-4 text-dark-text-secondary text-xs">
                  {formatDateTime(row.updated_at)}
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="inline-flex items-center gap-1">
                    {isDeleted ? (
                      <button
                        type="button"
                        onClick={() => handleRestore(row)}
                        className="p-1.5 rounded border border-dark-border text-dark-text-secondary hover:text-primary hover:border-primary transition-colors"
                        title={t('appVersion.actions.restore')}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          className="p-1.5 rounded border border-dark-border text-dark-text-secondary hover:text-primary hover:border-primary transition-colors"
                          title={t('appVersion.actions.edit')}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(row)}
                          className="p-1.5 rounded border border-dark-border text-dark-text-secondary hover:text-red-400 hover:border-red-400 transition-colors"
                          title={t('appVersion.actions.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </PaginatedTableCard>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-dark-card border border-dark-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-5 py-4 border-b border-dark-border">
              <h2 className="text-lg font-semibold text-dark-text">
                {editing
                  ? t('appVersion.modal.editTitle', { version: editing.version_name })
                  : t('appVersion.modal.createTitle')}
              </h2>
              <button
                type="button"
                className="text-dark-text-secondary hover:text-primary"
                onClick={closeModal}
                aria-label="close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label={t('appVersion.form.platform')} required>
                  <select
                    value={formValues.platform}
                    onChange={(e) => setFormValues((v) => ({ ...v, platform: e.target.value }))}
                    className="w-full bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-dark-text"
                  >
                    {PLATFORMS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label={t('appVersion.form.releaseStage')} required>
                  <select
                    value={formValues.release_stage}
                    onChange={(e) =>
                      setFormValues((v) => ({ ...v, release_stage: e.target.value }))
                    }
                    className="w-full bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-dark-text"
                  >
                    {RELEASE_STAGES.map((s) => (
                      <option key={s} value={s}>
                        {t(`appVersion.stage.${s}`)}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField
                  label={t('appVersion.form.versionName')}
                  required
                  hint={t('appVersion.form.versionNameHint')}
                  error={versionNameError}
                >
                  <input
                    type="text"
                    value={formValues.version_name}
                    onChange={(e) =>
                      setFormValues((v) => ({ ...v, version_name: e.target.value }))
                    }
                    placeholder="2.5.1"
                    className={`w-full bg-dark-surface border rounded-md px-3 py-2 text-dark-text ${
                      versionNameError ? 'border-red-500' : 'border-dark-border'
                    }`}
                  />
                </FormField>
                <FormField
                  label={t('appVersion.form.buildNumber')}
                >
                  <input
                    type="text"
                    value={formValues.build_number}
                    onChange={(e) =>
                      setFormValues((v) => ({ ...v, build_number: e.target.value }))
                    }
                    placeholder="2510"
                    className="w-full bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-dark-text"
                  />
                </FormField>
                <FormField label={t('appVersion.form.releasedAt')}>
                  <DateTimePicker
                    value={formValues.released_at}
                    onChange={(next) =>
                      setFormValues((v) => ({ ...v, released_at: next }))
                    }
                  />
                </FormField>
                <FormField label={t('appVersion.form.title')}>
                  <input
                    type="text"
                    value={formValues.title}
                    onChange={(e) => setFormValues((v) => ({ ...v, title: e.target.value }))}
                    className="w-full bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-dark-text"
                  />
                </FormField>
              </div>

              <FormField label={t('appVersion.form.description')}>
                <textarea
                  value={formValues.description}
                  onChange={(e) =>
                    setFormValues((v) => ({ ...v, description: e.target.value }))
                  }
                  rows={3}
                  className="w-full bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-dark-text"
                />
              </FormField>

              <FormField label={t('appVersion.form.internalNote')}>
                <textarea
                  value={formValues.internal_note}
                  onChange={(e) =>
                    setFormValues((v) => ({ ...v, internal_note: e.target.value }))
                  }
                  rows={3}
                  className="w-full bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-dark-text"
                />
              </FormField>

              {formError && (
                <div className="p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
                  {formError}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-dark-border -mx-5 px-5">
                <button
                  type="button"
                  className="px-4 py-2 rounded-md border border-dark-border text-dark-text-secondary hover:text-primary hover:border-primary transition-colors text-sm"
                  onClick={closeModal}
                >
                  {t('appVersion.form.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-2 rounded-md bg-primary text-black text-sm font-medium hover:bg-highlight transition-colors disabled:opacity-50"
                >
                  {isSaving ? t('common.loading') : t('appVersion.form.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
